import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthAPIMService } from '../authAPIMService/authAPIM.service';
import { PelotonGroup } from 'src/app/models/peloton-group.model';

export type PelotonAdminInstance = 'NA' | 'NA-Test' | 'East' | 'East-Test' | 'Local';
export { PelotonGroup };

@Injectable({
  providedIn: 'root'
})
export class PelotonAdminSvc {
  private readonly pelotonProdHost = environment.pelotonProdHost;
  private readonly pelotonTestHost = environment.pelotonTestHost;

  private baseHeaders = new HttpHeaders({
    'Ocp-Apim-Subscription-Key': environment.subscriptionKey
  });

  constructor(private httpClient: HttpClient, private authAPIMService: AuthAPIMService) {}

  public getPelotonAdminGroups(instance: PelotonAdminInstance): Observable<Array<{ id: string; name: string }>> {
    return this.authAPIMService.getAccessToken().pipe(
      switchMap(token => {
        if (!token) {
          return throwError(() => new Error('Unable to acquire access token for groups request.'));
        }
        const headers = this.baseHeaders.set('Authorization', `Bearer ${token}`);
        const url = this.buildGroupsUrl(instance);
        return this.httpClient.get<(PelotonGroup | string)[]>(url, { headers }).pipe(
          map(groups => groups.map(g =>
            typeof g === 'string'
              ? { id: g.trim(), name: g.trim() }
              : { id: (g as PelotonGroup).id, name: (g as PelotonGroup).name.trim() }
          ))
        );
      })
    );
  }

  public getPelotonGroupDetails(instance: PelotonAdminInstance, groupName: string): Observable<Record<string, unknown>> {
    const encodedGroupName = encodeURIComponent(groupName.trim());

    if (!encodedGroupName) {
      return throwError(() => new Error('A group name is required to retrieve group details.'));
    }

    return this.authAPIMService.getAccessToken().pipe(
      switchMap(token => {
        if (!token) {
          return throwError(() => new Error('Unable to acquire access token for group details request.'));
        }
        const headers = this.baseHeaders.set('Authorization', `Bearer ${token}`);
        const url = this.buildGroupDetailUrl(instance, encodedGroupName);
        return this.httpClient.get<Record<string, unknown>>(url, { headers });
      })
    );
  }

  private getHost(instance: PelotonAdminInstance): string {
    if (instance === 'Local') return environment.apiUrlAPIM;
    return (instance === 'NA-Test' || instance === 'East-Test') ? this.pelotonTestHost : this.pelotonProdHost;
  }

  private getRegionPath(instance: PelotonAdminInstance): string {
    return (instance === 'East' || instance === 'East-Test')
      ? 'pelotonadminv21east'
      : `pelotonadmin${environment.pelotonRegion}`;
  }

  private buildGroupsUrl(instance: PelotonAdminInstance): string {
    return `${this.getHost(instance)}/api/${this.getRegionPath(instance)}/Groups/All?key=1`;
  }

  private buildGroupDetailUrl(instance: PelotonAdminInstance, encodedGroupName: string): string {
    return `${this.getHost(instance)}/api/${this.getRegionPath(instance)}/Groups/${encodedGroupName}?key=1`;
  }
}
